import moment from 'moment';
import CrmLineItem from './CrmLineItem';
import Address from './Address';
import convertNumberToFinancialString from '../utility/convertNumberToFinancialString';

class CrmQuote {
  constructor(
    id,
    status,
    createdAt,
    grandTotal,
    tax,
    shippingAddress,
    billingAddress,
    productSpecialist,
    quoteLineItemList,
    expiresAt,
    shippingCost,
    shippingCarrier,
    shippingMethod,
    currency,
    crmOrgId,
    needsShipping,
  ) {
    this.id = id;
    this.status = status;
    this.createdAt = createdAt;
    this.grandTotal = grandTotal;
    this.tax = tax || '0';
    this.currency = currency;
    this.crmOrgId = crmOrgId;
    this.needsShipping = needsShipping;

    this.shippingAddress = shippingAddress
      ? new Address(
        shippingAddress.companyName,
        shippingAddress.contactName,
        shippingAddress.attention,
        shippingAddress.line1,
        shippingAddress.line2,
        shippingAddress.city,
        shippingAddress.state,
        shippingAddress.zipCode,
        shippingAddress.country,
        shippingAddress.phoneNumber,
        shippingAddress.id,
      ) : {};
    this.billingAddress = billingAddress
      ? new Address(
        billingAddress.companyName,
        billingAddress.contactName,
        billingAddress.attention,
        billingAddress.line1,
        billingAddress.line2,
        billingAddress.city,
        billingAddress.state,
        billingAddress.zipCode,
        billingAddress.country,
        billingAddress.phoneNumber,
        billingAddress.id,
      ) : {};
    this.productSpecialist = productSpecialist;
    this.quoteLineItemList = quoteLineItemList.map((lineItem) => new CrmLineItem(
      lineItem.productNumber,
      lineItem.description,
      lineItem.quantity,
      lineItem.discount,
      lineItem.listPrice,
      currency,
    ));
    this.expiresAt = expiresAt;
    this.shippingCost = shippingCost || '0';
    this.shippingCarrier = shippingCarrier;
    this.shippingMethod = shippingMethod;
  }

  get statusType() {
    if (this.status !== 'closed' && this.expiresAt != null) {
      return this.expDate;
    }
    return 'Closed';
  }

  get quantity() {
    return this.quoteLineItemList.length;
  }

  get quoteNumber() {
    return this.id !== null ? `QUO-${this.id}` : '-';
  }

  get total() {
    return this.grandTotal != null
      ? convertNumberToFinancialString(this.grandTotal, this.currency)
      : '-';
  }

  get subtotal() {
    return this.quoteLineItemList.reduce((sum, lineItem) => sum + lineItem.total, 0);
  }

  get expDate() {
    return this.expiresAt != null
      ? `Expires at ${moment.unix(this.expiresAt).format('MMM DD, YYYY')}`
      : null;
  }

  get taxDisplay() {
    return this.tax != null
      ? convertNumberToFinancialString(this.tax, this.currency)
      : convertNumberToFinancialString(0, this.currency);
  }

  get shippingCostDisplay() {
    return this.shippingCost != null
      ? convertNumberToFinancialString(this.shippingCost, this.currency)
      : convertNumberToFinancialString(0, this.currency);
  }

  get shippingDetailsString() {
    const shipCarrier = this.shippingCarrier != null ? `${this.shippingCarrier} ` : '';
    const shipMethod = this.shippingMethod != null ? this.shippingMethod : '';
    return shipCarrier + shipMethod;
  }

  get grandTotalDisplay() {
    const total = this.subtotal + parseFloat(this.tax) + parseFloat(this.shippingCost);
    return convertNumberToFinancialString(total, this.currency);
  }

  get createdAtDisplay() {
    return moment.unix(this.createdAt)
      .format('MMM DD, YYYY');
  }

  get expiresAtDisplay() {
    return moment.unix(this.expiresAt)
      .format('MMM DD, YYYY');
  }

  get hasShipment() {
    const normalize = (v) => String(v || '').trim().toLowerCase();
    return normalize(this.shippingMethod) !== 'no shipment'
      && normalize(this.shippingCarrier) !== 'no shipment';
  }
}
export default CrmQuote;
